import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import usePackageSearch from '@shared/v2/hooks/usePackageSearch';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import {
  handleDateDataLayerEvent,
  handleOccupantsDataLayerEvent,
} from '@/v2/utils/dataLayer/searchEvents/changeHandlers';
import { DateData, SearchPanelDataContextValue, TravellersData } from './types';

const SearchPanelDataContext = createContext<SearchPanelDataContextValue>({
  calendar: undefined,
  travellers: undefined,
  isDisabled: false,
});

const SearchPanelDataProvider = ({
  children: reactChildren,
}: {
  children: ReactNode;
}) => {
  const shouldUseNewEvents = useGA4Events();
  const { data, handleChangeData, handleSearch, validate, error } =
    usePackageSearch();
  const { departureDate, returnDate, adults, children, infants } = data;
  const { searchQuery } = useSearchQuery();

  // Common Disabled State
  const isDisabled = !searchQuery?.originCode || !searchQuery?.packageOption;

  // Calendar State + Handlers
  const handleCalendarChange = useCallback(
    (dates: DateData) => {
      if (shouldUseNewEvents) {
        handleDateDataLayerEvent(data, dates);
      }
      handleChangeData(dates);
      // trigger search if valid dates
      const { departureDate, returnDate } = validate(dates);
      if (!departureDate && !returnDate) {
        handleSearch(dates);
      }
    },
    [data, handleChangeData, handleSearch, shouldUseNewEvents, validate],
  );
  const calendarError = error?.departureDate || error?.returnDate;

  // Travellers State + Handlers
  const handleTravellersChange = useCallback(
    ({ infants, adults, children }: TravellersData) => {
      if (shouldUseNewEvents) {
        handleOccupantsDataLayerEvent(data, { infants, children, adults });
      }

      return handleChangeData({
        infants,
        adults,
        children,
      });
    },
    [data, handleChangeData, shouldUseNewEvents],
  );
  const travellersError = error?.travellers;

  // Context Value
  const value = useMemo(() => {
    return {
      calendar: {
        value: { departureDate, returnDate },
        onChange: handleCalendarChange,
        error: calendarError,
      },
      travellers: {
        value: { adults, children, infants },
        onChange: handleTravellersChange,
        error: travellersError,
      },
      isDisabled,
    };
  }, [
    adults,
    calendarError,
    children,
    departureDate,
    handleCalendarChange,
    handleTravellersChange,
    infants,
    isDisabled,
    returnDate,
    travellersError,
  ]);

  return (
    <SearchPanelDataContext.Provider value={value}>
      {reactChildren}
    </SearchPanelDataContext.Provider>
  );
};

const useSearchPanelData = () => useContext(SearchPanelDataContext);

export { SearchPanelDataProvider, useSearchPanelData };
